import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Theme system — 3 modes:
 *   - "system" (default): follows the OS preference, live-updates if it changes
 *   - "light": forces light, ignores OS
 *   - "dark":  forces dark, ignores OS
 *
 * Choice is persisted in localStorage under `lf_theme`. The `.dark` class is
 * toggled on <html> so Tailwind's `dark:` variants and our CSS `.dark { ... }`
 * tokens take effect.
 */
const ThemeContext = createContext(null);
const STORAGE_KEY = "lf_theme";
const VALID = ["system", "light", "dark"];

function getSystemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function readStored() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID.includes(v) ? v : "system";
  } catch {
    return "system";
  }
}

function applyHtmlClass(isDark) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  html.classList.toggle("dark", isDark);
  // Also expose the resolved theme as a data-attribute so non-Tailwind CSS
  // (e.g. third-party widgets) can target it if needed.
  html.dataset.theme = isDark ? "dark" : "light";
  // Sync the mobile browser chrome (Safari/Chrome iOS top-bar) to the active
  // theme. We update ALL existing <meta name="theme-color"> tags (including
  // the static fallback in index.html) so iOS Safari never falls back to a
  // wrong-tinted color when the app theme differs from the OS preference.
  // Light: #FAF7F2 (warm page background) · Dark: #020617 (slate-950)
  const color = isDark ? "#020617" : "#FAF7F2";
  const metas = document.querySelectorAll('meta[name="theme-color"]');
  if (metas.length === 0) {
    const m = document.createElement("meta");
    m.setAttribute("name", "theme-color");
    m.setAttribute("content", color);
    document.head.appendChild(m);
  } else {
    metas.forEach((m) => m.setAttribute("content", color));
  }
  // Also paint the html element itself — iOS Safari uses the html background
  // for the rubber-band overscroll zones at the very top and bottom of the
  // viewport. Without this the page canvas would be dark but the edges white.
  html.style.backgroundColor = color;
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => readStored());
  const [systemDark, setSystemDark] = useState(() => getSystemPrefersDark());

  // Listen for OS-level changes to prefers-color-scheme — keeps "system" mode
  // perfectly live without forcing the user to refresh.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemDark(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange); // legacy Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const isDark = mode === "dark" || (mode === "system" && systemDark);

  // Apply the resolved theme to <html> on every change.
  useEffect(() => {
    applyHtmlClass(isDark);
  }, [isDark]);

  const setTheme = useCallback((next) => {
    if (!VALID.includes(next)) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode — silent fallthrough */
    }
    setMode(next);
  }, []);

  // Cycle: system → light → dark → system
  const cycleTheme = useCallback(() => {
    setMode((m) => {
      const next = m === "system" ? "light" : m === "light" ? "dark" : "system";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ mode, isDark, setTheme, cycleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Soft fallback so consumers never crash if the provider is missing.
    return { mode: "system", isDark: false, setTheme: () => {}, cycleTheme: () => {} };
  }
  return ctx;
};
